/* eslint-disable camelcase */
import React, { Component } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { AxiosResponse } from 'axios';

import { SERVER_URL } from '../../config';
import { doubleDictionary } from '../../utils';
import { TFreelancerData } from '../../types';
import { TWeeklyHoursOption, TGenderOption } from './FreelancerDetails';
import FreelancerDetailsTextField from './common/FreelancerDetailsTextField';
import { ConfirmMarkAsUnsuitableModal } from './MarkUnsuitable';

type TFreelancerDetailContactFields = {
    first_name?: string;
    last_name?: string;
    mobile?: string;
    email?: string;
    birth_date?: string;
    birthDate?: string;
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
    account_number?: string;
    sort_code?: string;
    driving_licence_number?: string;
    national_insurance_no?: string;
    paye_statement?: any;
    tempest_payroll_number?: string;
    experience_and_qualifications?: string;
    authorisations_required?: string;
    max_weekly_hours?: number;
    gender?: string;
    is_archived: boolean;
    passed_presentation: boolean;
    is_unsuitable: boolean;
    share_code_student_restrictions_apply: boolean;
};

type TFreelancerDetailContactProps = {
    isAgent: boolean;
    isSuperUser: boolean;
    editionEnabled: boolean;
    freelancerData: TFreelancerData;
    weeklyHoursOptions: TWeeklyHoursOption[];
    genderOptions: TGenderOption[];
    onSave: (fields: any) => Promise<AxiosResponse<any, any>>;
};

type TFreelancerDetailContactState = {
    isEdit: boolean;
    isLoading: boolean;
    errorMessages: any[] | null;
    fields: TFreelancerDetailContactFields;
    isMarkUnsuitableModalOpen: boolean;
};

const payeDictionary: any = doubleDictionary({ FJ: 'A', OJ: 'B', AJ: 'C' });

class FreelancerDetailsContact extends Component<
    TFreelancerDetailContactProps,
    TFreelancerDetailContactState
> {
    constructor(props: TFreelancerDetailContactProps) {
        super(props);

        this.state = {
            isEdit: false,
            isLoading: false,
            errorMessages: null,
            fields: {
                first_name: '',
                last_name: '',
                mobile: '',
                email: '',
                birthDate: '',
                emergency_contact_name: '',
                emergency_contact_phone: '',
                account_number: '',
                sort_code: '',
                driving_licence_number: '',
                national_insurance_no: '',
                paye_statement: '',
                tempest_payroll_number: '',
                experience_and_qualifications: '',
                authorisations_required: '',
                max_weekly_hours: null,
                gender: '',
                is_archived: null,
                passed_presentation: null,
                is_unsuitable: false,
                share_code_student_restrictions_apply: false,
            },
            isMarkUnsuitableModalOpen: false,
        };

        this.handleEditButtonClick = this.handleEditButtonClick.bind(this);
        this.handleCancelButtonClick = this.handleCancelButtonClick.bind(this);
        this.handleSaveButtonClick = this.handleSaveButtonClick.bind(this);
        this.handleTextFieldChange = this.handleTextFieldChange.bind(this);
        this.handleMarkUnsuitableCancel = this.handleMarkUnsuitableCancel.bind(this);
        this.handleMarkUnsuitableConfirm = this.handleMarkUnsuitableConfirm.bind(this);
    }

    // eslint-disable-next-line react/no-deprecated
    componentWillUpdate(nextProps: any, nextState: { isEdit: boolean }) {
        if (nextState.isEdit !== this.state.isEdit) {
            this.initFields();
        }
    }

    initFields() {
        const { freelancerData } = this.props;
        this.setState({
            fields: {
                first_name: freelancerData.first_name || '',
                last_name: freelancerData.last_name || '',
                mobile: freelancerData.mobile || '',
                email: freelancerData.email || '',
                birthDate: freelancerData.birth_date
                    ? freelancerData.birth_date
                    : '',
                emergency_contact_name:
                    freelancerData.emergency_contact_name || '',
                emergency_contact_phone:
                    freelancerData.emergency_contact_phone || '',
                account_number: freelancerData.account_number || '',
                sort_code: freelancerData.sort_code || '',
                driving_licence_number: freelancerData.driving_licence_number || '',
                national_insurance_no:
                    freelancerData.national_insurance_no || '',
                tempest_payroll_number:
                    freelancerData.tempest_payroll_number || '',
                paye_statement: payeDictionary[freelancerData.paye_statement],
                experience_and_qualifications:
                    freelancerData.experience_and_qualifications || '',
                authorisations_required: freelancerData.authorisations_required || '',
                max_weekly_hours: freelancerData.max_weekly_hours,
                gender: freelancerData.gender,
                is_archived: freelancerData.is_archived,
                passed_presentation: freelancerData.passed_presentation,
                is_unsuitable: freelancerData.is_unsuitable,
                share_code_student_restrictions_apply: freelancerData.share_code_student_restrictions_apply,
            },
        });
    }

    handleTextFieldChange(
        event: React.ChangeEvent<HTMLInputElement>,
        fieldName: string,
    ) {
        let { value } = event.target;
        if (fieldName === 'national_insurance_no') {
            value = value.replace(/ /g, '');
        }
        this.setState(prevState => ({
            fields: { ...prevState.fields, [fieldName]: value },
        }));
    }

    handleSelect = (event: any, fieldName: string) => {
        this.setState(prevState => {
            const updatedFields: TFreelancerDetailContactFields = {
                ...prevState.fields,
                [fieldName]: event.value,
            };

            if (fieldName === 'max_weekly_hours') {
                // Only apply weekly hours logic if the field wasn't already set to true from backend
                if (!this.props.freelancerData.share_code_student_restrictions_apply) {
                    // If weekly hours is set to anything other than "No restriction" (value 0),
                    // automatically apply student share code restrictions
                    updatedFields.share_code_student_restrictions_apply = event.value !== 0;
                }
            }

            return { fields: updatedFields };
        });
    }

    handleMarkUnsuitableCancel() {
        this.setState({ isMarkUnsuitableModalOpen: false });
    }

    handleMarkUnsuitableConfirm() {
        if (this.props.freelancerData.has_upcoming_booking) {
            this.setState({
                errorMessages: ['Unable to mark worker as having found their own job. Worker has future bookings.'],
                isLoading: false,
            });
        } else {
            this.setState(prevState => ({
                fields: { ...prevState.fields, is_unsuitable: !prevState.fields.is_unsuitable },
            }));
            this.setState({ isMarkUnsuitableModalOpen: false });
        }
    }

    handleCheckboxFieldChange(
        event: React.ChangeEvent<HTMLInputElement>,
        fieldName: string,
    ) {
        if (fieldName === 'is_unsuitable') {
            this.setState({ isMarkUnsuitableModalOpen: true });
        } else {
            const { checked } = event.target;
            this.setState(prevState => ({
                fields: { ...prevState.fields, [fieldName]: checked },
            }));
        }

    }

    getWorkingHoursLabel = () => {
        const { freelancerData, weeklyHoursOptions } = this.props;
        const option = weeklyHoursOptions?.find(e => e.value === freelancerData.max_weekly_hours);

        if (option) {
            return option.label;
        }

        return freelancerData.max_weekly_hours;
    }

    getGenderLabel = () => {
        const { freelancerData, genderOptions } = this.props;
        const option = genderOptions?.find(e => e.value === freelancerData.gender);
        if (option) {
            return option.label;
        }

        return freelancerData.gender;
    }

    handleEditButtonClick() {
        this.setState({
            isEdit: true,
        });
    }

    handleCancelButtonClick() {
        this.setState({
            isEdit: false,
            errorMessages: null,
        });
    }

    handleDateChange = (date: any) => {
        if (date) {
            this.setState(prevState => ({
                fields: { ...prevState.fields, birthDate: date },
            }));
        }
    };

    uppercaseFirstLetter = (string: string) => {
        const toArr = string.replace(/_/g, ' ').split(' ');
        const toUpperCase = toArr.map(
            (word: string | any[]) => word[0].toUpperCase() + word.slice(1),
        );
        const newString = toUpperCase.join(' ');
        return newString;
    };

    handleSaveButtonClick() {
        const fields = {
            ...this.state.fields,
            birth_date: moment(this.state.fields.birthDate).format(
                'YYYY-MM-DD',
            ),
        };
        this.setState({
            isLoading: true,
            errorMessages: null,
        });
        fields.paye_statement = payeDictionary[fields.paye_statement];
        this.props
            .onSave(fields)
            .then(() => {
                this.setState({
                    isEdit: false,
                    isLoading: false,
                });
            })
            .catch(
                (error: { response: { data: { meta: { detail: any } } } }) => {
                    const messages: any[] = [];
                    const { detail } = error.response.data.meta;
                    const PART_OF_MESSAGE = /[t|T]his field/;

                    const setFieldNameInMessage = (
                        message: string,
                        fieldName: any,
                    ) => {
                        const newPartOfMessage = `"${this.uppercaseFirstLetter(
                            fieldName,
                        )}" field`;
                        const formattedMessage = message.replace(
                            PART_OF_MESSAGE,
                            newPartOfMessage,
                        );
                        return formattedMessage;
                    };
                    const getMessagesWithFieldNames = (
                        // eslint-disable-next-line no-shadow
                        detail: { [x: string]: any },
                        fieldName: string,
                    ) => {
                        const messagesWithoutFieldName = detail[fieldName];

                        messagesWithoutFieldName.forEach((message: string) => {
                            if (message.match(PART_OF_MESSAGE)) {
                                messages.push(
                                    setFieldNameInMessage(message, fieldName),
                                );
                            } else {
                                messages.push(message);
                            }
                        });

                        return messages[0];
                    };

                    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
                    const messagesWithFieldNames = Object.keys(detail).map(
                        fieldName =>
                            getMessagesWithFieldNames(detail, fieldName),
                    );

                    this.setState({
                        errorMessages: messages,
                        isLoading: false,
                    });
                },
            );
    }

    render() {
        // eslint-disable-next-line react/prop-types
        const { isAgent , isSuperUser, editionEnabled, freelancerData, weeklyHoursOptions, genderOptions } = this.props;
        const { isEdit, isLoading, errorMessages, fields } = this.state;
        return (
            <div className="freelancer-details-section">
                <div className="freelancer-details-section__header">
                    <div className="freelancer-details-section__title">
                        Contact
                    </div>
                    {editionEnabled && (
                        <div className="freelancer-details-section__btns">
                            {isSuperUser && (
                                <Link
                                    className="freelancer-details-section__btn"
                                    style={{ color: '#000' }}
                                    to={{ pathname: `${SERVER_URL}/admin/freelancer/freelancer/${freelancerData.id}` }}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <i className="icon icon-pencil-3"/>
                                    Back-office
                                </Link>
                            )}
                            {isEdit ? (
                                <>
                                    <button
                                        type="button"
                                        data-testid="saveBtn"
                                        disabled={isLoading}
                                        className="freelancer-details-section__btn"
                                        onClick={this.handleSaveButtonClick}
                                    >
                                        Save
                                    </button>
                                    <button
                                        data-testid="cancelBtn"
                                        type="button"
                                        disabled={isLoading}
                                        className="freelancer-details-section__btn"
                                        onClick={this.handleCancelButtonClick}
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    data-testid="editBtn"
                                    type="button"
                                    className="freelancer-details-section__btn"
                                    onClick={this.handleEditButtonClick}
                                >
                                    <i className="icon icon-pencil-3" />
                                    Edit Info
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <div>
                    {errorMessages && (
                        <div className="freelancer-details-section__errors">
                            {this.state.errorMessages.map((item: any) => (
                                <p data-testid="errorMsg" key={item.id}>
                                    {item}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
                <div className="freelancer-details-section__content">
                    <table className="table-default">
                        {isAgent ? (
                            <tbody>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Reference
                                    </td>
                                    <td>{freelancerData.reference_number}</td>
                                </tr>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Name
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <input
                                                data-testid="firstNameInput"
                                                type="text"
                                                value={fields.first_name}
                                                onChange={e => {
                                                    this.handleTextFieldChange(
                                                        e,
                                                        'first_name',
                                                    );
                                                }}
                                            />
                                            <input
                                                data-testid="lastNameInput"
                                                type="text"
                                                value={fields.last_name}
                                                onChange={e => {
                                                    this.handleTextFieldChange(
                                                        e,
                                                        'last_name',
                                                    );
                                                }}
                                            />
                                        </td>
                                    ) : (
                                        <td>{freelancerData.full_name}</td>
                                    )}
                                </tr>
                                <FreelancerDetailsTextField
                                    handleTextFieldChange={this.handleTextFieldChange}
                                    isEdit={isEdit}
                                    value={fields.mobile}
                                    apiField={freelancerData.mobile}
                                    fieldName="mobile"
                                    humanReadable="Mobile"
                                    testId="mobileInput"
                                />
                                <FreelancerDetailsTextField
                                    handleTextFieldChange={this.handleTextFieldChange}
                                    isEdit={isEdit}
                                    value={fields.email}
                                    apiField={freelancerData.email}
                                    fieldName="email"
                                    humanReadable="Email"
                                    testId="emailInput"
                                />
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Birth Date
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <DatePicker
                                                selected={
                                                    fields.birthDate
                                                        ? new Date(
                                                              fields.birthDate,
                                                          )
                                                        : null
                                                }
                                                onChange={this.handleDateChange}
                                                peekNextMonth={true}
                                                showMonthDropdown={true}
                                                showYearDropdown={true}
                                                dropdownMode="select"
                                                data-testid="birthDateInput"
                                                dateFormat="dd/MM/yyyy"
                                            />
                                        </td>
                                    ) : (
                                        <td>
                                            {freelancerData.birth_date
                                                ? moment(
                                                      freelancerData.birth_date,
                                                  ).format('DD/MM/YYYY')
                                                : 'N/A'}
                                        </td>
                                    )}
                                </tr>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Gender
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <Select
                                                name="gender"
                                                // @ts-ignore
                                                value={fields.gender}
                                                clearable={false}
                                                onChange={event => {
                                                    this.handleSelect(event, 'gender');
                                                }}
                                                options={genderOptions}
                                            />
                                        </td>
                                    ) : (
                                        <td>
                                            {this.getGenderLabel()}
                                        </td>
                                    )}
                                </tr>
                                <FreelancerDetailsTextField
                                    handleTextFieldChange={this.handleTextFieldChange}
                                    isEdit={isEdit}
                                    value={fields.emergency_contact_name}
                                    apiField={freelancerData.emergency_contact_name}
                                    fieldName="emergency_contact_name"
                                    humanReadable="Next of Kin Name"
                                    testId="kinNameInput"
                                />
                                <FreelancerDetailsTextField
                                    handleTextFieldChange={this.handleTextFieldChange}
                                    isEdit={isEdit}
                                    value={fields.emergency_contact_phone}
                                    apiField={freelancerData.emergency_contact_phone}
                                    fieldName="emergency_contact_phone"
                                    humanReadable="Next of Kin Number"
                                    testId="kinNumberInput"
                                />
                                <FreelancerDetailsTextField
                                    handleTextFieldChange={this.handleTextFieldChange}
                                    isEdit={isEdit}
                                    value={fields.national_insurance_no}
                                    apiField={freelancerData.national_insurance_no}
                                    fieldName="national_insurance_no"
                                    humanReadable="National Insurance"
                                    testId="insuranceInput"
                                />
                                <FreelancerDetailsTextField
                                    handleTextFieldChange={this.handleTextFieldChange}
                                    isEdit={isEdit}
                                    value={fields.tempest_payroll_number}
                                    apiField={freelancerData.tempest_payroll_number}
                                    fieldName="tempest_payroll_number"
                                    humanReadable="Tempest Payroll Number"
                                    testId="tempestInput"
                                />
                                <FreelancerDetailsTextField
                                    handleTextFieldChange={this.handleTextFieldChange}
                                    isEdit={isEdit}
                                    value={fields.paye_statement}
                                    apiField={payeDictionary[freelancerData.paye_statement]}
                                    fieldName="paye_statement"
                                    humanReadable="Starting Declaration"
                                    testId="declarationInput"
                                />
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Account Number
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <input
                                                data-testid="accountNumberInput"
                                                type="number"
                                                value={fields.account_number}
                                                onChange={e => {
                                                    // eslint-disable-next-line max-len
                                                    this.handleTextFieldChange(
                                                        e,
                                                        'account_number',
                                                    );
                                                }}
                                            />
                                        </td>
                                    ) : (
                                        <td>{freelancerData.account_number}</td>
                                    )}
                                </tr>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Sort Code
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <input
                                                data-testid="sortCodeInput"
                                                type="number"
                                                value={fields.sort_code}
                                                onChange={e => {
                                                    this.handleTextFieldChange(
                                                        e,
                                                        'sort_code',
                                                    );
                                                }}
                                            />
                                        </td>
                                    ) : (
                                        <td>{freelancerData.sort_code}</td>
                                    )}
                                </tr>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Driving Licence Number
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <input
                                                data-testid="drivingLicenceInput"
                                                type="text"
                                                value={fields.driving_licence_number}
                                                onChange={e => {
                                                    this.handleTextFieldChange(
                                                        e,
                                                        'driving_licence_number',
                                                    );
                                                }}
                                            />
                                        </td>
                                    ) : (
                                        <td>{freelancerData.driving_licence_number}</td>
                                    )}
                                </tr>
                                <FreelancerDetailsTextField
                                    handleTextFieldChange={this.handleTextFieldChange}
                                    isEdit={isEdit}
                                    value={fields.experience_and_qualifications}
                                    apiField={freelancerData.experience_and_qualifications}
                                    fieldName="experience_and_qualifications"
                                    humanReadable="Worker’s experience, qualifications and training"
                                    testId="experienceInput"
                                />
                                <FreelancerDetailsTextField
                                    handleTextFieldChange={this.handleTextFieldChange}
                                    isEdit={isEdit}
                                    value={fields.authorisations_required}
                                    apiField={freelancerData.authorisations_required}
                                    fieldName="authorisations_required"
                                    humanReadable="Authorisations required by law or by a professional body"
                                    testId="authorisationsInput"
                                />
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Maximum weekly working hours
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <Select
                                                name="maximum-working-hours"
                                                value={weeklyHoursOptions?.find(option => option.value === fields.max_weekly_hours)}
                                                clearable={false}
                                                onChange={event => {
                                                    this.handleSelect(event, 'max_weekly_hours');
                                                }}
                                                options={weeklyHoursOptions}
                                                disabled={freelancerData.max_weekly_hours !== 0}
                                            />
                                        </td>
                                    ) : (
                                        <td>
                                            {this.getWorkingHoursLabel()}
                                        </td>
                                    )}
                                </tr>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Archived
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <input
                                                data-testid="archivedInput"
                                                className="checkbox-purple"
                                                type="checkbox"
                                                checked={fields.is_archived}
                                                onChange={event => {
                                                    this.handleCheckboxFieldChange(
                                                        event,
                                                        'is_archived'
                                                    );
                                                }}
                                            />
                                        </td>
                                    ) : (
                                        <td>
                                            <i className={
                                                freelancerData.is_archived
                                                    ? 'icon icon-check-12'
                                                    : 'icon icon-close2'
                                            }/>
                                            <span style={{ paddingLeft: '10px' }}>
                                                {freelancerData.archived &&
                                                    `on ${moment(freelancerData.archived).format('DD/MM/YYYY')}`
                                                }
                                            </span>
                                        </td>
                                    )}
                                </tr>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Passed presentation
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <input
                                                data-testid="passedPresentationInput"
                                                className="checkbox-purple"
                                                type="checkbox"
                                                checked={fields.passed_presentation}
                                                onChange={event => {
                                                    this.handleCheckboxFieldChange(
                                                        event,
                                                        'passed_presentation'
                                                    );
                                                }}
                                                disabled={freelancerData.passed_presentation === true}
                                            />
                                        </td>
                                    ) : (
                                        <td>
                                            <i className={
                                                freelancerData.passed_presentation
                                                    ? 'icon icon-check-12'
                                                    : 'icon icon-close2'
                                            }/>
                                        </td>
                                    )}
                                </tr>
                                {freelancerData.published || freelancerData.is_unsuitable ? (
                                    <tr>
                                    <td className="table-default__cell--text-blue">
                                        Found Own Job selected
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <input
                                                data-testid="UnsuitableInput"
                                                className="checkbox-purple"
                                                type="checkbox"
                                                checked={fields.is_unsuitable}
                                                onChange={event => {
                                                    this.handleCheckboxFieldChange(
                                                        event,
                                                        'is_unsuitable'
                                                    );
                                                }}
                                            />
                                        </td>
                                    ) : (
                                        <td>
                                            <i className={
                                                freelancerData.is_unsuitable
                                                    ? 'icon icon-check-12'
                                                    : 'icon icon-close2'
                                            }/>
                                        </td>
                                    )}
                                </tr>
                                ) : null}
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Share Code Student Restrictions Apply
                                    </td>
                                    {isEdit ? (
                                        <td>
                                            <input
                                                data-testid="shareCodeStudentRestrictionsInput"
                                                className="checkbox-purple"
                                                type="checkbox"
                                                checked={fields.share_code_student_restrictions_apply}
                                                disabled={freelancerData.share_code_student_restrictions_apply || (!freelancerData.share_code_student_restrictions_apply && fields.max_weekly_hours !== 0)}
                                                onChange={event => {
                                                    this.handleCheckboxFieldChange(
                                                        event,
                                                        'share_code_student_restrictions_apply'
                                                    );
                                                }}
                                                style={(freelancerData.share_code_student_restrictions_apply || (!freelancerData.share_code_student_restrictions_apply && fields.max_weekly_hours !== 0)) ? { color: '#999' } : {}}
                                            />
                                        </td>
                                    ) : (
                                        <td>
                                            <i className={
                                                freelancerData.share_code_student_restrictions_apply
                                                    ? 'icon icon-check-12'
                                                    : 'icon icon-close2'
                                            }/>
                                        </td>
                                    )}
                                </tr>

                            </tbody>
                        ) : (
                            <tbody>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Name
                                    </td>
                                    <td>{freelancerData.full_name}</td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>
                {this.state.isMarkUnsuitableModalOpen && (
                    <ConfirmMarkAsUnsuitableModal
                        freelancer={freelancerData}
                        isOpen={this.state.isMarkUnsuitableModalOpen}
                        handleClose={this.handleMarkUnsuitableCancel}
                        onSave={this.handleMarkUnsuitableConfirm}
                        isLoading={isLoading}
                        isUnsuitable={fields.is_unsuitable}
                        error={errorMessages}
                    />
                )}
            </div>
        );
    }
}

export default FreelancerDetailsContact;
